import React, { useState, useMemo, Fragment, useEffect } from "react";
import { Box, Flex, Text, IconButton, Divider } from "@chakra-ui/react";
import { Formik } from "formik";
import CustomApprovalModal from "@/shared/components/custom-modal";
import CustomIcon from "../../../../../../../../public/icons-v1_1";
import useGetClaimableProducts from "@/features/my-order/features/order-history/features/claim/hooks/useGetClaimableProducts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import OrderHistoryClaimModalSelectQuantityStep from "./OrderHistoryClaimModalSelectQuantityStep";
import OrderHistoryClaimModalInputReasonStep from "@/features/my-order/features/order-history/features/claim/components/OrderHistoryClaimModalInputReasonStep";
import { useClaimModalStore } from "@/features/my-order/features/order-history/stores/useClaimModalStore";
import usePostClaims from "@/features/my-order/features/order-history/features/claim/hooks/usePostClaims";
import { convertClaimFormToRequest } from "@/features/my-order/features/order-history/features/claim/utils";
import { ClaimType } from "@/shared/types";

type Step = "SELECT_QUANTITY" | "INPUT_REASON";

interface ClaimDetail {
  reasonCategory: ClaimType;
  reasonDetail: string;
  images: string[];
  unoFileIds: number[];
}

interface ClaimItem {
  orderSheetProductOptionId: number;
  quantity: number;
  // UI 표시용 데이터
  brandName: string;
  sku: string;
  size: string;
  imageUrl: string;
  colorName: string;
  madeIn: string;
  category: string;
  claimAvailableQuantity: number;
  details: ClaimDetail[];
}

export interface ClaimModalFormValues {
  items: ClaimItem[];
}

function OrderHistoryClaimModal() {
  const { isOpen, onClose } = useClaimModalStore();
  const [step, setStep] = useState<Step>("SELECT_QUANTITY");
  const [searchTerm, setSearchTerm] = useState("");
  const { tTitle, isKor } = useI18nHelper();
  const { data: claimableProductsData } = useGetClaimableProducts();
  const { postClaims, isPending } = usePostClaims();

  // 평탄화된 상품 옵션 리스트
  const flattenedOptions = useMemo(() => {
    return (
      claimableProductsData?.confirmedProducts?.flatMap((product) =>
        product.options.map((option) => ({
          ...product,
          option,
        }))
      ) || []
    );
  }, [claimableProductsData]);

  const initialValues: ClaimModalFormValues = useMemo(() => {
    const items: ClaimItem[] = [];
    flattenedOptions.forEach((opt) => {
      items.push({
        orderSheetProductOptionId: opt.option.orderSheetProductOptionId,
        quantity: 0,
        brandName: opt.brandName,
        sku: opt.sku,
        size: opt.option.size,
        imageUrl: opt.imageUrl,
        colorName: opt.colorName,
        madeIn: opt.madeIn,
        category: opt.category,
        claimAvailableQuantity: opt.option.claimAvailableQuantity,
        details: [],
      });
    });
    return { items };
  }, [flattenedOptions]);

  const handleClose = (resetForm: () => void) => {
    setStep("SELECT_QUANTITY");
    setSearchTerm("");
    resetForm();
    onClose();
  };

  const filteredOptions = flattenedOptions.filter((item) => {
    return item.sku.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={async (values) => {
        const refinedForm = convertClaimFormToRequest(values);
        postClaims(refinedForm);
      }}
    >
      {({ values, resetForm, setFieldValue, submitForm }) => {
        const selectedTotalCount = values.items.reduce(
          (acc, cur) => acc + cur.quantity,
          0
        );

        const isInputReasonValid = values.items.every((item) => {
          if (item.quantity === 0) return true;

          return item.details.every((detail) => {
            // 사유 필수 체크 (MISSING 제외)
            if (
              detail.reasonCategory !== "MISSING" &&
              !detail.reasonDetail.trim()
            ) {
              return false;
            }

            // 이미지 필수 체크 (DAMAGED, INCORRECT)
            if (
              (detail.reasonCategory === "DAMAGED" ||
                detail.reasonCategory === "INCORRECT") &&
              detail.images.length === 0
            ) {
              return false;
            }

            return true;
          });
        });

        const handleNext = () => {
          if (step === "SELECT_QUANTITY") {
            setStep("INPUT_REASON");
          }
        };

        const handlePrev = () => {
          if (step === "INPUT_REASON") {
            setStep("SELECT_QUANTITY");
          }
        };

        return (
          <CustomApprovalModal
            isOpen={isOpen}
            onClose={() => handleClose(resetForm)}
            isCentered
            height={"650px"}
            closeOnOverlayClick={false}
          >
            <CustomApprovalModal.Header
              title={
                <Flex alignItems="center">
                  {step === "INPUT_REASON" && (
                    <IconButton
                      aria-label="back"
                      icon={
                        <CustomIcon
                          src="chevron-left"
                          width={24}
                          height={24}
                          color={"#8F8F8C"}
                        />
                      }
                      variant="ghost"
                      size="xs"
                      onClick={handlePrev}
                      _hover={{
                        background: "inherit",
                      }}
                      transform={"translateY(1px)"}
                    />
                  )}
                  <Text textStyle={"body-md-bold"} color={"#444444"}>
                    {tTitle("order_history:claim.request_claim")}
                  </Text>
                </Flex>
              }
            />
            <CustomApprovalModal.Body>
              {step === "SELECT_QUANTITY" ? (
                <OrderHistoryClaimModalSelectQuantityStep
                  searchTerm={searchTerm}
                  setSearchTerm={(val) => setSearchTerm(val)}
                  filteredOptions={filteredOptions}
                />
              ) : (
                <OrderHistoryClaimModalInputReasonStep />
              )}
            </CustomApprovalModal.Body>
            <Divider color={"#D4D4D1"} opacity={0.8} />
            <CustomApprovalModal.Footer
              cancelName="취소"
              confirmName={step === "SELECT_QUANTITY" ? "다음" : "요청"}
              isLoading={isPending}
              onCancel={() => handleClose(resetForm)}
              onConfirm={step === "SELECT_QUANTITY" ? handleNext : submitForm}
              isConfirmDisabled={
                (step === "SELECT_QUANTITY" && selectedTotalCount === 0) ||
                (step === "INPUT_REASON" && !isInputReasonValid)
              }
            />
            {step === "SELECT_QUANTITY" && selectedTotalCount > 0 && (
              <Box position={"absolute"} bottom={"16px"} left={"16px"}>
                <Text textStyle="body-sm-regular" color="#444444">
                  {isKor ? "총 " : "Total "}
                  <Text as="span" fontWeight="bold" color="#1272EF">
                    {selectedTotalCount}
                  </Text>
                  {tTitle("common:search.selected")}
                </Text>
              </Box>
            )}
          </CustomApprovalModal>
        );
      }}
    </Formik>
  );
}

export default OrderHistoryClaimModal;
